import React, { useLayoutEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import SubmitButton from '../shared/submit-button'
import { AuthModalBody } from '../auth-modal/auth-modal-body'

import useAuth from '../../hooks/use-auth'
import { facebookCompleteSchema } from '../../validations/auth-schemas'
import { buildFacebookRegisterPayload } from '../../utils/build-signup-payload'
import { mapFacebookRegisterServerErrors } from '../../utils/map-server-errors'
import {
  AuthCheckboxField,
  AuthTextField,
  Logo,
  Title,
} from '../shared'

const buildNickFromFacebook = (fbData) =>
  fbData.name.replace(' ', '').substr(0, 8) +
  (Math.floor(Math.random() * (9999 - 1000)) + 1000)

export default function CompleteFacebookForm () {
  const { submitting, fbData, postFacebookRegister } = useAuth()
  const usernameRef = useRef()

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { isValid },
  } = useForm({
    resolver: zodResolver(facebookCompleteSchema),
    defaultValues: {
      nick: '',
      name: '',
      lastName: '',
      referalCode: '',
      email: '',
      password: '',
      cpassword: '',
      acceptTerms: false,
      checkAge: false,
      checkGetNews: true,
    },
    mode: 'onChange',
  })

  useLayoutEffect(() => {
    if (!fbData) return

    reset({
      nick: buildNickFromFacebook(fbData),
      name: fbData.first_name || '',
      lastName: fbData.last_name || '',
      referalCode: '',
      email: fbData.email || '',
      password: '',
      cpassword: '',
      acceptTerms: false,
      checkAge: false,
      checkGetNews: true,
    })

    const end = usernameRef.current?.value?.length || 0
    usernameRef.current?.setSelectionRange(end, end)
    usernameRef.current?.focus()
  }, [fbData, reset])

  const onSubmit = async (formValues) => {
    const formData = await buildFacebookRegisterPayload({ formValues, fbData })

    postFacebookRegister(formData, (errors) => {
      const errorObjectList = mapFacebookRegisterServerErrors(errors)
      Object.entries(errorObjectList).forEach(([field, message]) => {
        setError(field, { type: 'server', message })
      })
    })
  }

  const canSubmit = isValid && !submitting

  return (
    <AuthModalBody>
      <Logo src='/images/Pickwin.png' alt='Pickwin logo' />
      <Title>Completar Registro</Title>

      <AuthTextField
        control={control}
        name='nick'
        placeholder='Nombre de usuario'
        ref={usernameRef}
        iconSrc='/images/Icono usuario.png'
        autoComplete='username'
      />

      {!fbData?.name && (
        <AuthTextField control={control} name='name' placeholder='Nombre' />
      )}

      {!fbData?.last_name && (
        <AuthTextField control={control} name='lastName' placeholder='Apellido' />
      )}

      {!fbData?.email && (
        <AuthTextField
          control={control}
          name='email'
          placeholder='Email'
          iconSrc='/images/Icono Mail.png'
          autoComplete='email'
        />
      )}

      <AuthTextField
        control={control}
        name='password'
        placeholder='Contraseña'
        iconSrc='/images/Icono candado.png'
        type='password'
        autoComplete='new-password'
      />
      <AuthTextField
        control={control}
        name='cpassword'
        placeholder='Confirmar Contraseña'
        iconSrc='/images/Icono candado.png'
        type='password'
        autoComplete='new-password'
      />
      <AuthTextField
        control={control}
        name='referalCode'
        placeholder='Código de referido'
        iconSrc='/images/Icono codigo.png'
        style={{ marginBottom: '1em' }}
      />

      <AuthCheckboxField
        control={control}
        name='acceptTerms'
        label='Acepto términos y condiciones del sitio'
      />
      <AuthCheckboxField
        control={control}
        name='checkAge'
        label='Certifico que soy mayor de 18 años'
      />
      <AuthCheckboxField
        control={control}
        name='checkGetNews'
        label='Acepto recibir comunicados de Pickwin'
      />

      <SubmitButton
        style={{ marginTop: '1.5em' }}
        loading={submitting === 'facebook-loading'}
        onClick={handleSubmit(onSubmit)}
        disabled={!canSubmit}
      >
        Completar Registro
      </SubmitButton>
    </AuthModalBody>
  )
}
